import type { Task } from '@portfolio-pm/shared';
import type { getDocStore } from '../../../store/doc-store.ts';
import type { TaskGroupBy, TaskSort, TaskSystemViewId } from '../task-workspace-model.ts';

/**
 * Shared option lists, small types, and constants for the Tasks
 * workspace. Extracted from TaskWorkspace.tsx so the toolbar, list
 * pane, dialogs, and mobile selector modules can share them without a
 * circular import back into the workspace component.
 */

export const SYSTEM_VIEWS: Array<{ id: TaskSystemViewId; label: string }> = [
  { id: 'all', label: 'All Tasks' },
  { id: 'today', label: 'Today' },
  { id: 'tomorrow', label: 'Tomorrow' },
  { id: 'week', label: 'This Week' },
  { id: 'favorites', label: 'Favourites' },
  { id: 'trash', label: 'Trash' },
];

export const MOBILE_TASK_VIEW_QUERY = '(max-width: 767px)';
export const TASK_SWIPE_THRESHOLD_RATIO = 0.2;
export const TASK_SWIPE_INTENT_PX = 8;

export type TaskDropPlacement = 'before' | 'after';

export interface TaskDragState {
  movingTaskIds: string[];
  targetGroupId?: string;
  targetTaskId?: string;
  placement?: TaskDropPlacement;
}

export const TASK_GROUP_OPTIONS: Array<{ value: TaskGroupBy; label: string }> = [
  { value: 'none', label: 'No groups' },
  { value: 'project', label: 'Project' },
  { value: 'tag', label: 'Tag' },
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'scheduled', label: 'Scheduled date' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'owner', label: 'Owner' },
];

export const TASK_SORT_OPTIONS: Array<{ value: TaskSort['key']; label: string }> = [
  { value: 'manual', label: 'Manual order' },
  { value: 'name', label: 'Task name' },
  { value: 'priority', label: 'Priority' },
  { value: 'due', label: 'Due date' },
  { value: 'scheduled', label: 'Scheduled date' },
  { value: 'created', label: 'Created' },
  { value: 'updated', label: 'Updated' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'project', label: 'Project' },
  { value: 'tag', label: 'Tag' },
];

export type SelectionPresetId = 'all' | 'none' | 'overdue' | 'due-today' | 'due-tomorrow';
export type PostponeOptionId = '1d' | '2d' | '3d' | '4d' | '1w' | '2w';
export type PostponeDelayUnit = 'days' | 'weeks' | 'months' | 'years';
export type DateToolbarField = 'dueAt' | 'scheduledAt';
export type TaskPatch = Parameters<ReturnType<typeof getDocStore>['updateTask']>[1];

export const SELECTION_PRESETS: Array<{ id: SelectionPresetId; label: string }> = [
  { id: 'all', label: 'Select all visible' },
  { id: 'none', label: 'Select none' },
  { id: 'overdue', label: 'Select overdue' },
  { id: 'due-today', label: 'Select due today' },
  { id: 'due-tomorrow', label: 'Select due tomorrow' },
];

export const POSTPONE_OPTIONS: Array<{
  id: PostponeOptionId;
  label: string;
  delay: { amount: number; unit: PostponeDelayUnit };
}> = [
  { id: '1d', label: '1 day', delay: { amount: 1, unit: 'days' } },
  { id: '2d', label: '2 days', delay: { amount: 2, unit: 'days' } },
  { id: '3d', label: '3 days', delay: { amount: 3, unit: 'days' } },
  { id: '4d', label: '4 days', delay: { amount: 4, unit: 'days' } },
  { id: '1w', label: '1 week', delay: { amount: 1, unit: 'weeks' } },
  { id: '2w', label: '2 weeks', delay: { amount: 2, unit: 'weeks' } },
];

export const POSTPONE_DELAY_UNITS: Array<{ id: PostponeDelayUnit; label: string }> = [
  { id: 'days', label: 'days' },
  { id: 'weeks', label: 'weeks' },
  { id: 'months', label: 'months' },
  { id: 'years', label: 'years' },
];

export const PRIORITY_OPTIONS: Array<{ id: Task['priority']; label: string }> = [
  { id: 1, label: 'P1' },
  { id: 2, label: 'P2' },
  { id: 3, label: 'P3' },
  { id: 0, label: 'None' },
];

export const DEFAULT_TAG_COLORS = [
  '#dc2626',
  '#ea580c',
  '#d97706',
  '#ca8a04',
  '#65a30d',
  '#16a34a',
  '#059669',
  '#0d9488',
  '#0891b2',
  '#0284c7',
  '#2563eb',
  '#4f46e5',
  '#7c3aed',
  '#9333ea',
  '#c026d3',
  '#db2777',
  '#e11d48',
  '#64748b',
  '#78716c',
  '#57534e',
  '#1f2937',
];

export const ENERGY_OPTIONS: Array<{ value: NonNullable<Task['energy']>; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'med', label: 'Medium' },
  { value: 'high', label: 'High' },
];

export const DAYPART_OPTIONS: Array<{ value: NonNullable<Task['daypart']>; label: string }> = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
];

export interface BulkEditDraft {
  owner: string;
  url: string;
  estimateMinutes: string;
  location: string;
  recurrence: string;
  reminderAt: string;
  notes: string;
  energy: '' | NonNullable<Task['energy']>;
  daypart: '' | NonNullable<Task['daypart']>;
}

export const EMPTY_BULK_EDIT_DRAFT: BulkEditDraft = {
  owner: '',
  url: '',
  estimateMinutes: '',
  location: '',
  recurrence: '',
  reminderAt: '',
  notes: '',
  energy: '',
  daypart: '',
};
